import { MapClient } from "@roomservice/react";
import { atom, useAtom } from "jotai";
import { INITIAL_EAR_HEIGHT } from "../av/PositionalAudio";
import {
  UserOnlines,
  User,
  UserLocation,
  Space,
  FaceBlock,
  Point2D,
} from "../types";
import { gridToPixelPosition, gridToPixelSize } from "../utils/gridPosition";
import { Client, Stream } from "agora-rtc-sdk";
import { atomWithReducer } from "jotai/utils";

// Application
export const currentUserAtom = atom<User | undefined>(undefined);
export const currentSpaceAtom = atom<Space | undefined>(undefined);
export function useSpace() {
  const [currentSpace] = useAtom(currentSpaceAtom);
  return currentSpace;
}
export const spaceListAtom = atom<string[]>([]);

export const onlinesAtom = atom<UserOnlines>([]);
export function useOnlines() {
  const [onlines] = useAtom(onlinesAtom);
  return onlines;
}

const selectionReducer = (
  prev: Set<string>,
  action: { type: "clear" | "add" | "set"; id?: string }
) => {
  let { type, id } = action;
  if (type === "clear") {
    if (prev.size === 0) {
      return prev;
    }
    return new Set([]);
  }

  if (!id) {
    throw new Error("called selection without id");
  }
  if (type === "add") {
    return new Set([...prev, id]);
  } else {
    return new Set([id]);
  }
};

export const blockSelectionAtom = atomWithReducer(
  new Set([]),
  selectionReducer
);

type BlockIdsMap = Record<string, string>; // blockId, blockId because maps are easier to manage than list and order isn't important
export const blockIdsAtom = atom<MapClient<BlockIdsMap> | undefined>(undefined);

export const faceBlocksAtom = atom<Record<string, FaceBlock>>({});
export const blockYouJustCreatedAtom = atom<string | undefined>(undefined);

export const updateFaceBlocksAtom = atom(null, (get, set, block: FaceBlock) =>
  set(faceBlocksAtom, { ...get(faceBlocksAtom), [block.id]: { ...block } })
);

export const deleteFromFaceBlocksAtom = atom(null, (get, set, id: string) => {
  let fba = get(faceBlocksAtom);
  if (fba[id]) {
    delete fba[id];
    return set(faceBlocksAtom, { ...fba });
  }
});

export const userLocationsAtom = atom<Record<string, UserLocation>>((get) => {
  const blocks = get(faceBlocksAtom);
  const onlines = get(onlinesAtom);
  const locations: Record<string, UserLocation> = {};
  for (const id in blocks) {
    const block = blocks[id];
    if (block.value && onlines.includes(block.value)) {
      locations[block.value] = {
        block: block.id,
        position: gridToPixelPosition(block.position),
        size: gridToPixelSize(block.size),
        locked: block.lockedBy !== undefined,
      };
    }
  }
  return locations;
});

export const agoraClientAtom = atom<Client | undefined>(undefined);
export const streamsAtom = atom<Record<string, Stream>>({});
// Used to mute audio before a user input has been recorded to satisfy chrome's Audio restriction
export const hasGestureAtom = atom(false);
export const consoleOnAtom = atom(false);
export const bugReportOnAtom = atom(false);
export const pannersAtom = atom<Record<string, PannerNode>>({});
export const earHeightAtom = atom(INITIAL_EAR_HEIGHT);
export const deadZoneAtom = atom(INITIAL_EAR_HEIGHT + 100);
export const rollOffAtom = atom(6);

export const canvasRefAtom = atom<HTMLDivElement | undefined>(undefined);
export function useCanvasRef() {
  const [canvasRef] = useAtom(canvasRefAtom);
  return canvasRef;
}
export const mouseRefAtom = atom<React.MutableRefObject<Point2D> | undefined>(
  undefined
);
export function useMouseRef() {
  const [mouseRef] = useAtom(mouseRefAtom);
  return mouseRef;
}
export const scrollRefAtom = atom<React.MutableRefObject<Point2D> | undefined>(
  undefined
);
export function useScrollRef() {
  const [scrollRef] = useAtom(scrollRefAtom);
  return scrollRef;
}

export const defaultSpringStiffnessAtom = atom(345);
export const defaultSpringDampingAtom = atom(28);
export const defaultSpringMassAtom = atom(1);
export const defaultSpringAtom = atom((get) => {
  return {
    stiffness: get(defaultSpringStiffnessAtom),
    damping: get(defaultSpringDampingAtom),
    mass: get(defaultSpringMassAtom),
  };
});
